import { useQueries } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import { catalogApi } from '@/api/endpoints/catalog';
import { useQueryClient } from '@tanstack/react-query';
import { Dialog } from '@/app/layers/Dialog';
import { findVideoInCaches, videoQueryKey } from '@/features/video/useVideo';
import { useTranslator } from '@/i18n/LocaleProvider';
import { cx } from '@/lib/cx';
import styles from './AddToCanvasPicker.module.css';
import { useFreeTier } from '@/access/useFreeTier';
import { useCanvasActions, useCanvases, type SaveSource } from './useCanvases';

interface PickerRequest {
  ids: number[];
  source: SaveSource;
  searchEventId: number | null;
}

interface PickerContextValue {
  openPicker: (ids: number[], source: SaveSource, searchEventId?: number | null) => void;
  closePicker: () => void;
}

const PickerContext = createContext<PickerContextValue | null>(null);

/** Окно выбора канваса (CNV-20): принимает 1..N id, `add` / `on canvas · remove`, «+ New canvas» создаёт и добавляет. */
export function AddToCanvasPickerProvider({ children }: { children: ReactNode }) {
  const [request, setRequest] = useState<PickerRequest | null>(null);

  const openPicker = useCallback(
    (ids: number[], source: SaveSource, searchEventId: number | null = null) => {
      if (ids.length === 0) return;
      setRequest({ ids, source, searchEventId });
    },
    [],
  );
  const closePicker = useCallback(() => setRequest(null), []);

  const value = useMemo(() => ({ openPicker, closePicker }), [openPicker, closePicker]);

  return (
    <PickerContext.Provider value={value}>
      {children}
      {request ? <PickerDialog request={request} onClose={closePicker} /> : null}
    </PickerContext.Provider>
  );
}

export function useAddToCanvasPicker(): PickerContextValue {
  const value = useContext(PickerContext);
  if (!value)
    throw new Error('useAddToCanvasPicker must be used inside <AddToCanvasPickerProvider>');
  return value;
}

function PickerDialog({ request, onClose }: { request: PickerRequest; onClose: () => void }) {
  const { ids, source, searchEventId } = request;
  const queryClient = useQueryClient();
  const { data: canvases = [] } = useCanvases();
  const { addToCanvas, removeFromCanvas, createAndAdd, isPending } = useCanvasActions();
  const { guardCreateCanvas } = useFreeTier();

  // Состав канвасов у выбранных видео — из кеша выдачи или точечно.
  const videos = useQueries({
    queries: ids.map((id) => ({
      queryKey: videoQueryKey(id),
      queryFn: async () => (await catalogApi.show(id)).data,
      initialData: () => findVideoInCaches(queryClient, id),
      initialDataUpdatedAt: () => Date.now(),
      staleTime: 60_000,
    })),
  });
  const membership = videos.map((q) => q.data?.saved_canvas_ids ?? []);
  const { t, tp } = useTranslator();

  const title = ids.length > 1 ? t('picker.titleCount', { n: ids.length }) : t('picker.title');

  const pick = async (canvasId: number, has: boolean) => {
    if (has) {
      await Promise.all(ids.map((id) => removeFromCanvas(canvasId, id, source)));
      return;
    }
    const missing = ids.filter((_id, i) => !(membership[i] ?? []).includes(canvasId));
    if (missing.length) await addToCanvas(canvasId, missing, source, searchEventId);
    onClose();
  };

  const createNew = async () => {
    if (!guardCreateCanvas()) return;
    await createAndAdd(ids, source, searchEventId);
    onClose();
  };

  return (
    <Dialog
      open
      onClose={onClose}
      labelledBy="picker-title"
      maxWidth={420}
      sheetOnMobile
      className={styles.panel}
    >
      <div className={styles.head}>
        <span id="picker-title" className={styles.title}>
          {title}
        </span>
        <button
          type="button"
          className={styles.close}
          aria-label={t('common.close')}
          onClick={onClose}
        >
          ×
        </button>
      </div>
      {canvases.map((canvas) => {
        const has = ids.length > 0 && membership.every((list) => list.includes(canvas.id));
        return (
          <button
            key={canvas.id}
            type="button"
            className={cx(styles.row, has && styles.rowOn)}
            disabled={isPending}
            onClick={() => void pick(canvas.id, has)}
          >
            {canvas.cover_url ? (
              <img src={canvas.cover_url} alt="" className={styles.cover} />
            ) : (
              <span className={styles.cover} />
            )}
            <span className={styles.info}>
              <span className={styles.name} data-clarity-mask="true">
                {canvas.name}
              </span>
              <span className={styles.meta}>
                {canvas.is_default ? t('picker.defaultTag') : ''}
                {tp('catalog.itemsCount', canvas.nodes_count)}
              </span>
            </span>
            <span className={cx(styles.state, has && styles.stateOn)}>
              {has ? t('picker.onCanvasRemove') : t('picker.add')}
            </span>
          </button>
        );
      })}
      <button
        type="button"
        className={styles.new}
        disabled={isPending}
        onClick={() => void createNew()}
      >
        {t('catalog.newCanvas')}
      </button>
    </Dialog>
  );
}
